import { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MOCK_COMMAND_SUGGESTIONS } from "../../data/mockArchie";

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

interface QuickAction {
  label: string;
  icon: React.ReactNode;
  iconColor: string;
  path: string | null;
}

// Inline SVG icons
const SearchIcon = () => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    sx={{ width: 15, height: 15 }}
  >
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </Box>
);

const GridIcon = () => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    sx={{ width: 15, height: 15 }}
  >
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </Box>
);

const DownloadIcon = () => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    sx={{ width: 15, height: 15 }}
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </Box>
);

const ShieldIcon = () => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    sx={{ width: 15, height: 15 }}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </Box>
);

const ArchieIcon = () => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    sx={{ width: 13, height: 13, color: "#fff" }}
  >
    <path d="M12 2a4 4 0 0 0-4 4v2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-2V6a4 4 0 0 0-4-4z" /><circle cx="9" cy="13" r="1" /><circle cx="15" cy="13" r="1" />
  </Box>
);

const SUGGESTION_COUNT = MOCK_COMMAND_SUGGESTIONS.length;
const QUICK_ACTION_COUNT = 4;
const TOTAL_ITEMS = SUGGESTION_COUNT + QUICK_ACTION_COUNT;

export default function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const { t } = useTranslation(["archie", "menuBar", "common"]);
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();
  const base = `/customers/${customerId}/v2`;

  const QUICK_ACTIONS: QuickAction[] = [
    { label: t("menuBar:searchInteractions"), icon: <SearchIcon />, iconColor: "#10B981", path: "/v2/search" },
    { label: t("menuBar:goToDashboard"), icon: <GridIcon />, iconColor: "#0EA5E9", path: "/v2/dashboard" },
    { label: t("menuBar:startBulkExport"), icon: <DownloadIcon />, iconColor: "#F59E0B", path: "/v2/jobs/bulk-export" },
    { label: t("menuBar:runComplianceScan"), icon: <ShieldIcon />, iconColor: "#F59E0B", path: null },
  ];

  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset selection when opening
  useEffect(() => {
    if (open) {
      setSelectedIndex(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % TOTAL_ITEMS);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + TOTAL_ITEMS) % TOTAL_ITEMS);
      } else if (e.key === "Enter") {
        // Quick actions start after suggestions
        const quickActionIndex = selectedIndex - SUGGESTION_COUNT;
        if (quickActionIndex >= 0) {
          const action = QUICK_ACTIONS[quickActionIndex];
          if (action.path) {
            onClose();
            navigate(`${base}${action.path}`);
          }
        }
      }
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose, selectedIndex, navigate, base]);

  if (!open) return null;

  const handleQuickAction = (action: QuickAction) => {
    if (action.path) {
      onClose();
      navigate(`${base}${action.path}`);
    } else {
      onClose();
    }
  };

  const kbdSx = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    px: "6px",
    py: "2px",
    borderRadius: "4px",
    border: "1px solid var(--border-mid)",
    backgroundColor: "var(--bg-raised)",
    color: "var(--text-secondary)",
    fontSize: "0.6875rem",
    fontFamily: "inherit",
    lineHeight: 1.4,
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        pt: "12vh",
        backdropFilter: "blur(4px)",
        backgroundColor: "rgba(0,0,0,0.45)",
      }}
      onClick={onClose}
    >
      {/* Palette container — stop propagation so clicks inside don't close */}
      <Box
        sx={{
          width: 640,
          maxHeight: 480,
          display: "flex",
          flexDirection: "column",
          borderRadius: "12px",
          border: "1px solid var(--archie-border)",
          backgroundColor: "var(--bg-surface)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.2)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: "20px",
            py: "16px",
            borderBottom: "1px solid var(--border-subtle)",
            flexShrink: 0,
          }}
        >
          {/* Archie avatar */}
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "var(--archie-gradient)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ArchieIcon />
          </Box>

          {/* Input */}
          <Box
            component="input"
            ref={inputRef}
            type="text"
            placeholder={t("archie:askOrCommand")}
            autoFocus
            sx={{
              flex: 1,
              fontSize: "0.9375rem",
              fontFamily: "inherit",
              color: "var(--text-primary)",
              backgroundColor: "transparent",
              border: "none",
              outline: "none",
              "&::placeholder": {
                color: "var(--text-muted, var(--text-secondary))",
                opacity: 0.7,
              },
            }}
          />

          {/* ESC badge */}
          <Box component="span" sx={{ ...kbdSx, flexShrink: 0 }}>
            ESC
          </Box>
        </Box>

        {/* Body — scrollable */}
        <Box sx={{ overflowY: "auto", flex: 1 }}>
          {/* Suggestions section */}
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            <Box
              sx={{
                px: 1,
                py: 0.5,
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--text-secondary)",
                mb: 0.5,
              }}
            >
              {t("archie:suggestions")}
            </Box>
            {MOCK_COMMAND_SUGGESTIONS.map((suggestion: { text: string; subtitle: string }, i: number) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 1.5,
                  py: 1,
                  borderRadius: "8px",
                  cursor: "pointer",
                  backgroundColor: selectedIndex === i ? "var(--bg-hover)" : "transparent",
                  "&:hover": { backgroundColor: "var(--bg-hover)" },
                }}
                onClick={() => onClose()}
              >
                {/* Small archie icon */}
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: "var(--archie-gradient)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    opacity: 0.8,
                  }}
                >
                  <Box
                    component="svg"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.75}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    sx={{ width: 11, height: 11, color: "#fff" }}
                  >
                    <path d="M12 2a4 4 0 0 0-4 4v2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-2V6a4 4 0 0 0-4-4z" /><circle cx="9" cy="13" r="1" /><circle cx="15" cy="13" r="1" />
                  </Box>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      fontSize: "0.875rem",
                      color: "var(--text-primary)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {suggestion.text}
                  </Box>
                  <Box
                    sx={{
                      fontSize: "0.75rem",
                      color: "var(--text-secondary)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      mt: 0.25,
                    }}
                  >
                    {suggestion.subtitle}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Quick Actions section */}
          <Box sx={{ px: 2, pt: 0.5, pb: 1.5 }}>
            <Box
              sx={{
                px: 1,
                py: 0.5,
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--text-secondary)",
                mb: 0.5,
              }}
            >
              {t("archie:quickActions")}
            </Box>
            {QUICK_ACTIONS.map((action, i) => {
              const globalIndex = SUGGESTION_COUNT + i;
              return (
                <Box
                  key={action.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 1.5,
                    py: 1,
                    borderRadius: "8px",
                    cursor: "pointer",
                    backgroundColor: selectedIndex === globalIndex ? "var(--bg-hover)" : "transparent",
                    "&:hover": { backgroundColor: "var(--bg-hover)" },
                  }}
                  onClick={() => handleQuickAction(action)}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "6px",
                      backgroundColor: `${action.iconColor}22`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      color: action.iconColor,
                    }}
                  >
                    {action.icon}
                  </Box>
                  <Box
                    sx={{
                      fontSize: "0.875rem",
                      color: "var(--text-primary)",
                    }}
                  >
                    {action.label}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderTop: "1px solid var(--border-subtle)",
            flexShrink: 0,
          }}
        >
          <Box component="kbd" sx={kbdSx}>↑</Box>
          <Box component="kbd" sx={kbdSx}>↓</Box>
          <Box sx={{ fontSize: "0.6875rem", color: "var(--text-secondary)", mr: 1 }}>{t("archie:navigate")}</Box>

          <Box component="kbd" sx={kbdSx}>↵</Box>
          <Box sx={{ fontSize: "0.6875rem", color: "var(--text-secondary)", mr: 1 }}>{t("archie:select")}</Box>

          <Box component="kbd" sx={kbdSx}>esc</Box>
          <Box sx={{ fontSize: "0.6875rem", color: "var(--text-secondary)" }}>{t("common:close")}</Box>

          <Box sx={{ ml: "auto", fontSize: "0.6875rem", color: "var(--archie-text, var(--text-secondary))" }}>
            {t("archie:poweredByArchie")}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
